import * as THREE from 'three';
import { GameObject, ObjectType } from './GameObject';
import { ObjectManager } from './ObjectManager';
import { Config } from '../core/Config';
import { LODSystem } from '../optimization/LODSystem';
import { StaticObject } from './static/StaticObject';
// Import LOD classes
import { LODTree } from './static/LODTree';
import { LODRock } from './static/LODRock';
import { LODBush } from './static/LODBush';
import { LODLoaf } from './static/LODLoaf';
import { DynamicObject, UpdatePriority } from './dynamic/DynamicObject';
// Import toaster classes
import { LODToasterSmall } from './dynamic/LODToasterSmall';
import { LODToasterMedium } from './dynamic/LODToasterMedium';
import { LODToasterLarge } from './dynamic/LODToasterLarge';
import { LODToaster } from './dynamic/LODToaster';
import { GameState } from '../game/GameState';
// Import our new classes
import { BlenderTower } from './dynamic/BlenderTower';
import { Engine } from '../core/Engine';
import { ProjectileSystem } from '../projectiles/ProjectileSystem';


/**
 * Manages pools of toaster objects for reuse
 */
class ToasterPool {
  // Separate pools for each toaster type
  private smallToasterPool: LODToasterSmall[] = [];
  private mediumToasterPool: LODToasterMedium[] = [];
  private largeToasterPool: LODToasterLarge[] = [];
  
  // Active toasters tracking
  private activeSmallToasters: LODToasterSmall[] = [];
  private activeMediumToasters: LODToasterMedium[] = [];
  private activeLargeToasters: LODToasterLarge[] = [];
  
  // Pool configuration
  private smallPoolSize: number = 20;
  private mediumPoolSize: number = 15;
  private largePoolSize: number = 10;
  
  // Tracking stats
  private totalCreated: { [key: string]: number } = {
    'SMALL': 0,
    'MEDIUM': 0,
    'LARGE': 0
  };
  private poolReuses: { [key: string]: number } = {
    'SMALL': 0,
    'MEDIUM': 0,
    'LARGE': 0
  };
  
  // Reference to object manager for adding/removing objects
  private objectManager: ObjectManager;
  
  constructor(objectManager: ObjectManager) {
    this.objectManager = objectManager;
  }
  
  /**
   * Initialize the toaster pools with configurable sizes
   */
  public initialize(smallPoolSize?: number, mediumPoolSize?: number, largePoolSize?: number): void {
    // Set pool sizes if provided
    if (smallPoolSize !== undefined) this.smallPoolSize = smallPoolSize;
    if (mediumPoolSize !== undefined) this.mediumPoolSize = mediumPoolSize;
    if (largePoolSize !== undefined) this.largePoolSize = largePoolSize;
    
    // Initialize pools
    this.initializePool('SMALL', this.smallPoolSize);
    this.initializePool('MEDIUM', this.mediumPoolSize);
    this.initializePool('LARGE', this.largePoolSize);
    
    console.log(`Toaster pools initialized: Small(${this.smallPoolSize}), Medium(${this.mediumPoolSize}), Large(${this.largePoolSize})`);
  }
  
  /**
   * Initialize a specific toaster pool
   */
  private initializePool(toasterType: string, poolSize: number): void {
    for (let i = 0; i < poolSize; i++) {
      const toaster = this.createToasterInstance(toasterType);
      if (toaster) {
        // Set toaster to inactive state
        toaster.setActive(false);
        
        // Add to appropriate pool
        this.addToPool(toasterType, toaster);
        
        // Track total created
        this.totalCreated[toasterType]++;
      }
    }
  }
  
  /**
   * Create a new toaster instance of specified type
   */
  private createToasterInstance(toasterType: string): LODToaster | null {
    switch (toasterType) {
      case 'SMALL':
        return new LODToasterSmall();
      case 'MEDIUM':
        return new LODToasterMedium();
      case 'LARGE':
        return new LODToasterLarge();
      default:
        console.error(`Unknown toaster type: ${toasterType}`);
        return null;
    }
  }
  
  /**
   * Add a toaster to its appropriate pool
   */
  private addToPool(toasterType: string, toaster: LODToaster): void {
    switch (toasterType) {
      case 'SMALL':
        this.smallToasterPool.push(toaster as LODToasterSmall);
        break;
      case 'MEDIUM':
        this.mediumToasterPool.push(toaster as LODToasterMedium);
        break;
      case 'LARGE':
        this.largeToasterPool.push(toaster as LODToasterLarge);
        break;
    }
  }
  
  /**
   * Get a toaster from the pool or create a new one if needed
   */
  public getToaster(toasterType: string): LODToaster | null {
    let toaster: LODToaster | null = null;
    
    console.log(`Getting ${toasterType} toaster from pool. Current pool sizes: Small(${this.smallToasterPool.length}), Medium(${this.mediumToasterPool.length}), Large(${this.largeToasterPool.length})`);
    
    // Try to get from appropriate pool
    switch (toasterType) {
      case 'SMALL':
        if (this.smallToasterPool.length > 0) {
          toaster = this.smallToasterPool.pop()!;
          this.activeSmallToasters.push(toaster as LODToasterSmall);
          this.poolReuses.SMALL++;
          console.log(`Reused ${toasterType} toaster from pool (reuses: ${this.poolReuses.SMALL})`);
        } else {
          // Create new if pool is empty
          toaster = new LODToasterSmall();
          this.activeSmallToasters.push(toaster as LODToasterSmall);
          this.totalCreated.SMALL++;
          console.log(`Small toaster pool depleted, created new toaster (total: ${this.totalCreated.SMALL})`);
        }
        break;
        
      case 'MEDIUM':
        if (this.mediumToasterPool.length > 0) {
          toaster = this.mediumToasterPool.pop()!;
          this.activeMediumToasters.push(toaster as LODToasterMedium);
          this.poolReuses.MEDIUM++;
          console.log(`Reused ${toasterType} toaster from pool (reuses: ${this.poolReuses.MEDIUM})`);
        } else {
          // Create new if pool is empty
          toaster = new LODToasterMedium();
          this.activeMediumToasters.push(toaster as LODToasterMedium);
          this.totalCreated.MEDIUM++;
          console.log(`Medium toaster pool depleted, created new toaster (total: ${this.totalCreated.MEDIUM})`);
        }
        break;
        
      case 'LARGE':
        if (this.largeToasterPool.length > 0) {
          toaster = this.largeToasterPool.pop()!;
          this.activeLargeToasters.push(toaster as LODToasterLarge);
          this.poolReuses.LARGE++;
          console.log(`Reused ${toasterType} toaster from pool (reuses: ${this.poolReuses.LARGE})`);
        } else {
          // Create new if pool is empty
          toaster = new LODToasterLarge();
          this.activeLargeToasters.push(toaster as LODToasterLarge);
          this.totalCreated.LARGE++;
          console.log(`Large toaster pool depleted, created new toaster (total: ${this.totalCreated.LARGE})`);
        }
        break;
        
      default:
        console.error(`Unknown toaster type: ${toasterType}`);
        return null;
    }
    
    if (toaster) {
      // Sanity check the mesh
      if (!toaster.getMesh()) {
        console.error(`Toaster ${toaster.getId()} has no mesh!.`);
      }
      
      // Activate the toaster
      toaster.setActive(true);
      
      // Add to object manager if not already added
      if (!this.objectManager.has(toaster.getId())) {
        console.log(`Adding toaster ${toaster.getId()} to object manager`);
        this.objectManager.add(toaster);
      } else {
        console.log(`Toaster ${toaster.getId()} already in object manager`);
      }
    }
    
    console.log(`Returning ${toasterType} toaster with ID: ${toaster?.getId() || 'null'}`);
    return toaster;
  }
  
  /**
   * Return a toaster to its appropriate pool
   */
  public returnToPool(toaster: LODToaster): void {
    // Deactivate the toaster
    toaster.setActive(false);
    
    // Remove from active lists and add back to pool
    if (toaster instanceof LODToasterSmall) {
      const index = this.activeSmallToasters.indexOf(toaster);
      if (index !== -1) {
        this.activeSmallToasters.splice(index, 1);
      }
      this.smallToasterPool.push(toaster);
    } else if (toaster instanceof LODToasterMedium) {
      const index = this.activeMediumToasters.indexOf(toaster);
      if (index !== -1) {
        this.activeMediumToasters.splice(index, 1);
      }
      this.mediumToasterPool.push(toaster);
    } else if (toaster instanceof LODToasterLarge) {
      const index = this.activeLargeToasters.indexOf(toaster);
      if (index !== -1) {
        this.activeLargeToasters.splice(index, 1);
      }
      this.largeToasterPool.push(toaster);
    }
  }
  
  /**
   * Get pool usage statistics
   */
  public getPoolStats(): { created: { [key: string]: number }, reuses: { [key: string]: number } } {
    return {
      created: { ...this.totalCreated },
      reuses: { ...this.poolReuses }
    };
  }
  
  /**
   * Get counts of active toasters by type
   */
  public getActiveToasterCounts(): { small: number, medium: number, large: number, total: number } {
    return {
      small: this.activeSmallToasters.length,
      medium: this.activeMediumToasters.length,
      large: this.activeLargeToasters.length,
      total: this.activeSmallToasters.length + this.activeMediumToasters.length + this.activeLargeToasters.length
    };
  }
}

/**
 * Factory class for creating game objects
 */
export class ObjectFactory {
  private objectManager: ObjectManager;
  private scene: THREE.Scene;
  private breadLoaf: LODLoaf | null = null;
  private toasters: LODToaster[] = [];
  private toasterPool: ToasterPool;
  private gameState: GameState;
  
  // Add properties for blender towers
  private blenderTowers: BlenderTower[] = [];
  
  // Add a property to store ProjectileSystem
  private projectileSystem: ProjectileSystem | null = null;
  
  constructor(objectManager: ObjectManager, scene: THREE.Scene, gameState: GameState) {
    this.objectManager = objectManager;
    this.scene = scene;
    this.gameState = gameState;
    
    // Set the scene on StaticObject so instanced meshes can be added
    StaticObject.setScene(this.scene);
    
    // Initialize toaster pool
    this.toasterPool = new ToasterPool(objectManager);
    
    console.log('ObjectFactory initialized');
  }
  
  /**
   * Create all game objects based on configuration
   */
  public createAllObjects(): GameObject[] {
    // First create all static objects
    const staticObjects = this.createRandomStaticObjects();
    
    // Create a bread loaf for toasters to target
    this.createBreadLoaf();
    
    // No initial towers - players must place them during the game
    
    // Initialize toaster pools for later use by the LevelManager
    this.initializeToasterPools();
    
    // Combine all objects
    const allObjects = [...staticObjects, ...this.toasters];
    if (this.breadLoaf) {
      allObjects.push(this.breadLoaf);
    }
    
    console.log(`Created total of ${allObjects.length} objects`);
    
    return allObjects;
  }
  
  /**
   * Create a bread loaf at a random central position
   */
  public createBreadLoaf(): LODLoaf | null {
    // Create a new bread loaf
    this.breadLoaf = new LODLoaf();
    
    // Calculate a random position in the central area
    // Random position within 30% of the center
    const terrainSize = Config.TERRAIN_SIZE;
    const centerArea = terrainSize * 0.3;
    const offsetX = (Math.random() - 0.5) * centerArea;
    const offsetZ = (Math.random() - 0.5) * centerArea;
    
    // Position it slightly above ground
    this.breadLoaf.setPosition(offsetX, -0.1, offsetZ);
    
    // Add to the object manager
    this.objectManager.add(this.breadLoaf);
    
    // Register the position with the game state
    this.gameState.setLoafPosition(this.breadLoaf.getPosition());
    
    console.log(`Bread loaf placed at: ${offsetX}, 0.1, ${offsetZ}`);
    
    return this.breadLoaf;
  }
  
  /**
   * Reset bread loaf and toasters
   */
  public resetToasters(): void {
    // Remove existing objects
    if (this.breadLoaf) {
      this.objectManager.remove(this.breadLoaf.getId());
      this.breadLoaf = null;
    }
    
    for (const toaster of this.toasters) {
      this.objectManager.remove(toaster.getId());
    }
    this.toasters = [];
    
    // Reset game state
    this.gameState.reset();
    
    // Create new bread loaf
    this.createBreadLoaf();
    
    // Note: We don't create new toasters here since that's now handled by the LevelManager
    
    console.log('Game reset with new bread loaf position');
  }
  
  // Create random static objects across the terrain
  public createRandomStaticObjects(): GameObject[] {
    const objects: GameObject[] = [];
    
    // Create trees
    const trees = this.createRandomLODTrees(Config.STATIC_OBJECT_COUNT.TREES);
    objects.push(...trees);
    
    // Create rocks
    const rocks = this.createRandomLODRocks(Config.STATIC_OBJECT_COUNT.ROCKS);
    objects.push(...rocks);
    
    // Create bushes
    const bushes = this.createRandomLODBushes(Config.STATIC_OBJECT_COUNT.BUSHES);
    objects.push(...bushes);
    
    console.log(`Created ${objects.length} static objects with LOD:`);
    console.log(`- ${trees.length} trees`);
    console.log(`- ${rocks.length} rocks`);
    console.log(`- ${bushes.length} bushes`);
    
    return objects;
  }
  
  // Create random LOD trees
  private createRandomLODTrees(count: number): GameObject[] {
    const trees: GameObject[] = [];
    const terrainSize = Config.TERRAIN_SIZE;
    const halfSize = terrainSize / 2;
    
    for (let i = 0; i < count; i++) {
      const tree = new LODTree();
      
      // Random position - initially set y to 0
      const x = Math.random() * terrainSize - halfSize;
      const z = Math.random() * terrainSize - halfSize;
      
      // Random rotation
      const rotY = Math.random() * Math.PI * 2;
      
      // Apply rotation
      tree.setRotation(0, rotY, 0);
      
      // Create height variation here - trees should be tall but have some variation
      const heightVar = 0.3 + Math.random() * 0.3; 
      // Width can vary a bit more
      const widthVar = 1.5 + Math.random() * 0.6;
      
      // Get the base scale to apply variations
      const baseScale = tree.getScale();
      
      // Apply scale variations - height and width can vary independently
      tree.setScale(
        baseScale.x * widthVar,
        baseScale.y * heightVar,
        baseScale.z * widthVar
      );
      
      // Calculate correct ground position:
      // - The tree pivot is at the center of height 
      // - We want the tree bottom at y=0 (ground level)
      // - Add a slight embed offset to place it slightly into the ground
      const scaledHeight = baseScale.y * heightVar;
      const embedOffset = 2; // Slightly higher embed value to ensure grounding
      const y = scaledHeight / 2 - embedOffset;
      
      // Apply final position with correct y-offset for ground placement
      tree.setPosition(x, y, z);
      
      // Add to object manager
      this.objectManager.add(tree);
      trees.push(tree);
    }
    
    return trees;
  }
  
  // Create random LOD rocks
  private createRandomLODRocks(count: number): GameObject[] {
    const rocks: GameObject[] = [];
    const terrainSize = Config.TERRAIN_SIZE;
    const halfSize = terrainSize / 2;
    
    for (let i = 0; i < count; i++) {
      const rock = new LODRock();
      
      // Random position - initially set y to 0
      const x = Math.random() * terrainSize - halfSize;
      const z = Math.random() * terrainSize - halfSize;
      
      // Random rotation and scale variation
      const rotY = Math.random() * Math.PI * 2;
      const scaleVar = 0.5 + Math.random() * 1.0; // 0.5 to 1.5
      
      // First set the position without y-offset and apply rotation
      rock.setPosition(x, 0, z);
      rock.setRotation(0, rotY, 0);
      
      // Apply scale variation while maintaining proportions
      const baseScale = rock.getScale();
      rock.setScale(
        baseScale.x * scaleVar,
        baseScale.y * scaleVar,
        baseScale.z * scaleVar
      );
      
      // Calculate y position based on scaled height and embed offset
      const scaledHeight = baseScale.y * scaleVar;
      const embedOffset = 0.15;
      const y = scaledHeight / 2 - embedOffset;
      
      // Apply final position with correct y-offset
      rock.setPosition(x, y, z);
      
      // Add to object manager
      this.objectManager.add(rock);
      rocks.push(rock);
    }
    
    return rocks;
  }
  
  // Create random LOD bushes
  private createRandomLODBushes(count: number): GameObject[] {
    const bushes: GameObject[] = [];
    const terrainSize = Config.TERRAIN_SIZE;
    const halfSize = terrainSize / 2;
    
    for (let i = 0; i < count; i++) {
      const bush = new LODBush();
      
      // Random position - initially set y to 0
      const x = Math.random() * terrainSize - halfSize;
      const z = Math.random() * terrainSize - halfSize;
      
      // Random rotation and scale variation
      const rotY = Math.random() * Math.PI * 2;
      const scaleVar = 0.6 + Math.random() * 0.8; // 0.6 to 1.4
      
      // First set the position without y-offset and apply rotation
      bush.setPosition(x, 0, z);
      bush.setRotation(0, rotY, 0);
      
      // Apply scale variation while maintaining proportions
      const baseScale = bush.getScale();
      bush.setScale(
        baseScale.x * scaleVar,
        baseScale.y * scaleVar,
        baseScale.z * scaleVar
      );
      
      // Calculate y position based on scaled height and embed offset
      const scaledHeight = baseScale.y * scaleVar;
      const embedOffset = 0.15;
      const y = scaledHeight / 2 - embedOffset;
      
      // Apply final position with correct y-offset
      bush.setPosition(x, y, z);
      
      // Add to object manager
      this.objectManager.add(bush);
      bushes.push(bush);
    }
    
    return bushes;
  }
  
  /**
   * Create a toaster of specified type at a given position
   */
  public createToasterOfType(toasterType: string, x: number, z: number): GameObject | null {
    // Get a toaster from the pool
    let toaster: LODToaster | null = this.toasterPool.getToaster(toasterType);
    
    if (!toaster) {
      console.error(`Failed to get ${toasterType} toaster from pool`);
      return null;
    }
    
    // Prepare the toaster for use - this handles positioning, visibility, and pathfinding
    toaster.prepareForUse(x, z);
    
    console.log(`Created ${toasterType} toaster at (${x}, 0.5, ${z})`);
    
    // Add to our internal list if not already there
    if (!this.toasters.includes(toaster)) {
      this.toasters.push(toaster);
    }
    
    return toaster;
  }
  
  /**
   * Initialize the toaster pools with given sizes
   */
  public initializeToasterPools(smallPoolSize?: number, mediumPoolSize?: number, largePoolSize?: number): void {
    this.toasterPool.initialize(smallPoolSize, mediumPoolSize, largePoolSize);
  }
  
  /**
   * Return a toaster to the pool
   */
  public returnToasterToPool(toaster: LODToaster): void {
    // Remove from our internal list
    const index = this.toasters.indexOf(toaster);
    if (index !== -1) {
      this.toasters.splice(index, 1);
    }
    
    // Return to pool
    this.toasterPool.returnToPool(toaster);
  }
  
  /**
   * Get toaster pool statistics
   */
  public getToasterPoolStats(): any {
    return this.toasterPool.getPoolStats();
  }
  
  /**
   * Get counts of active toasters by type
   */
  public getActiveToasterCounts(): any {
    return this.toasterPool.getActiveToasterCounts();
  }
  
  /**
   * Get the bread loaf object
   */
  public getBreadLoaf(): LODLoaf | null {
    return this.breadLoaf;
  }
  
  /**
   * Get all toasters
   */
  public getToasters(): LODToaster[] {
    return this.toasters;
  }
  
  /**
   * Set the projectile system for this factory
   */
  public setProjectileSystem(projectileSystem: ProjectileSystem): void {
    this.projectileSystem = projectileSystem;
    console.log('Projectile system set in ObjectFactory:', !!this.projectileSystem);
    
    // Update any existing BlenderTowers with the projectile system
    if (this.blenderTowers.length > 0) {
      console.log(`Updating ${this.blenderTowers.length} existing BlenderTowers with projectileSystem`);
      this.updateBlenderTowersWithProjectileSystem();
    }
  }
  
  /**
   * Update all existing BlenderTowers with the current projectileSystem
   */
  private updateBlenderTowersWithProjectileSystem(): void {
    if (!this.projectileSystem) {
      console.warn('Cannot update BlenderTowers: no projectileSystem available');
      return;
    }
    
    for (const tower of this.blenderTowers) {
      console.log(`Setting projectileSystem on tower ${tower.getName()}`);
      tower.setProjectileSystem(this.projectileSystem);
    }
  }
  
  /**
   * Create a set of blender towers around the bread loaf
   * @param count Number of towers to create
   * @returns Array of created towers
   */
  public createBlenderTowers(count: number = 3): BlenderTower[] {
    console.log(`Creating ${count} blender towers with projectileSystem:`, !!this.projectileSystem);
    
    // Clear existing towers
    this.blenderTowers = [];
    
    // Use our stored projectileSystem instead of getting it from Engine
    // to break the circular dependency
    
    // Get loaf position to place towers around it
    const loafPosition = this.breadLoaf?.getPosition() || new THREE.Vector3(0, 0, 0);
    
    // Create towers in a circle around the loaf
    for (let i = 0; i < count; i++) {
      // Calculate position in a circle
      const angle = (i / count) * Math.PI * 2;
      const distance = 30; // 30 units from loaf
      const x = loafPosition.x + Math.cos(angle) * distance;
      const z = loafPosition.z + Math.sin(angle) * distance;
      
      // Debug the projectileSystem right before creating the tower
      console.log(`Creating BlenderTower_${i} with projectileSystem:`, !!this.projectileSystem);
      
      // Create tower - pass the projectileSystem
      const tower = new BlenderTower(
        this.objectManager,
        this.projectileSystem,
        `BlenderTower_${i}`
      );
      tower.setPosition(x, 0, z);
      
      // Add to object manager
      this.objectManager.add(tower);
      
      // Store reference
      this.blenderTowers.push(tower);
    }
    
    return this.blenderTowers;
  }
  
  /**
   * Create a single blender tower at a specific position
   * @param x X position
   * @param z Z position
   * @returns The created tower
   */
  public createBlenderTower(x: number, z: number): BlenderTower | null {
    console.log(`Creating a single blender tower at position (${x}, 0, ${z})`);
    
    if (!this.objectManager) {
      console.error("Cannot create tower: ObjectManager not available");
      return null;
    }
    
    // Create a unique name for this tower
    const towerCount = this.blenderTowers.length;
    const towerName = `BlenderTower_Player_${towerCount}`;
    
    // Create tower with the projectileSystem
    const tower = new BlenderTower(
      this.objectManager,
      this.projectileSystem,
      towerName
    );
    
    // Set position (y=0 to ensure it's on the ground)
    tower.setPosition(x, 0, z);
    
    // Add to object manager
    this.objectManager.add(tower);
    
    // Store reference
    this.blenderTowers.push(tower);
    
    console.log(`Successfully created tower: ${towerName}`);
    return tower;
  }
  
  /**
   * Get all blender towers
   */
  public getBlenderTowers(): BlenderTower[] {
    return this.blenderTowers;
  }
}
